// Feedbacks Manager Component
import React, { useState, useEffect } from 'react';
import { Trash2, Loader2, AlertTriangle, Handshake, Building2, User, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FEEDBACKS = 100;

const FeedbacksManager = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchFeedbacks(); }, []);

  const fetchFeedbacks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/feedbacks`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setFeedbacks(data || []); }
    } catch (err) { console.error('Error fetching feedbacks:', err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/feedbacks/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Demande supprimée');
      fetchFeedbacks();
    } catch (err) { 
      console.error('Error deleting feedback:', err); 
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer TOUTES les ${feedbacks.length} demandes ?\n\nCette action est irréversible !`)) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/feedbacks`, { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchFeedbacks();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (err) { 
      console.error('Error deleting all feedbacks:', err); 
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Demandes de Partenariat</h2>
          <p className="text-sm text-slate-500">
            {feedbacks.length} demande{feedbacks.length !== 1 ? 's' : ''} 
            <span className="text-slate-400"> (max {MAX_FEEDBACKS} conservées)</span>
          </p>
        </div>
        {feedbacks.length > 0 && (
          <Button 
            variant="outline" 
            onClick={handleDeleteAll}
            disabled={deleting}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Tout supprimer
          </Button>
        )}
      </div>

      {/* Warning if approaching limit */}
      {feedbacks.length >= MAX_FEEDBACKS * 0.8 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Attention :</strong> Vous approchez de la limite de {MAX_FEEDBACKS} demandes. 
            Les anciennes demandes seront automatiquement supprimées.
          </span>
        </div>
      )}

      <div className="space-y-4">
        {feedbacks.map((fb) => (
          <div key={fb.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{fb.company_name || 'Entreprise non spécifiée'}</h3>
                    <p className="text-sm text-slate-500">{fb.full_name || 'Contact non spécifié'}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{fb.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{fb.phone || '-'}</span>
                  </div>
                  {fb.capital && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        Capital: {fb.capital}
                      </span>
                    </div>
                  )}
                </div>

                {/* Address */}
                {fb.address && (
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span className="text-sm text-slate-600">{fb.address}</span>
                  </div>
                )}

                {/* Message */}
                {fb.message && (
                  <div className="bg-slate-50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{fb.message}</p>
                  </div>
                )}

                {/* Date */}
                <p className="text-xs text-slate-400 mt-3">
                  Reçue le {new Date(fb.created_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(fb.id)} title="Supprimer">
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {feedbacks.length === 0 && (
        <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-xl">
          <Handshake className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Aucune demande de partenariat.</p>
        </div>
      )}
    </div>
  );
};

export default FeedbacksManager;
